import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { ArticleCard } from '../components/ArticleCard';
import { Archive, BookOpen, Calendar, ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ArchivesPage() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedNumero, setSelectedNumero] = useState(null);
  const [numeros, setNumeros] = useState([]);
  const [stats, setStats] = useState({ total_articles: 0, numeros_count: 0 });

  useEffect(() => {
    fetchArchives();
  }, []);

  const fetchArchives = async () => {
    try {
      // Use dedicated archives endpoint to get all PDF articles
      const response = await fetch(`${API_URL}/api/archives?limit=500`);
      const data = await response.json();
      
      setArticles(data.articles || []);
      setNumeros(data.numeros || []);
      setStats({
        total_articles: data.total_articles || 0,
        numeros_count: data.stats?.numeros_count || 0
      });
    } catch (error) {
      console.error('Error fetching archives:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredArticles = selectedNumero
    ? articles.filter(a => a.journal_numero === selectedNumero)
    : articles;

  return (
    <div className="min-h-screen bg-stone-50">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-stone-800 to-stone-900 rounded-lg p-8 md:p-12 mb-12 text-white">
          <div className="flex items-center gap-3 mb-4">
            <Archive className="h-8 w-8 text-red-400" />
            <h1 className="font-playfair text-3xl md:text-4xl font-bold">
              Archives VersaillesPlus
            </h1>
          </div>
          
          <div className="max-w-3xl">
            <p className="font-lora text-lg text-stone-300 mb-6 leading-relaxed">
              Depuis février 2007, VersaillesPlus chronique la vie versaillaise avec un regard libre et indépendant. 
              Nous reconstituons progressivement nos archives pour vous permettre de redécouvrir 
              près de <strong className="text-white">20 ans d'histoire locale</strong>.
            </p>
            
            <div className="bg-amber-500/20 border border-amber-500/40 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <BookOpen className="h-5 w-5 text-amber-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-inter text-sm text-amber-200 font-medium mb-1">
                    Reconstitution en cours
                  </p>
                  <p className="font-inter text-sm text-stone-400">
                    Nos équipes numérisent et indexent les anciens numéros de VersaillesPlus. 
                    De nouveaux articles sont ajoutés régulièrement. Certains contenus peuvent 
                    être incomplets ou en cours de vérification.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-4 text-sm font-inter">
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                <Calendar className="h-4 w-4 text-red-400" />
                <span>Depuis 2007</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                <Archive className="h-4 w-4 text-red-400" />
                <span>{stats.total_articles.toLocaleString()} articles numérisés</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                <BookOpen className="h-4 w-4 text-red-400" />
                <span>{stats.numeros_count} numéros disponibles</span>
              </div>
            </div>
          </div>
        </div>

        {/* Filter by Numero */}
        {numeros.length > 0 && (
          <div className="mb-8">
            <h2 className="font-playfair text-xl font-semibold text-stone-900 mb-4">
              Parcourir par numéro
            </h2>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedNumero(null)}
                className={`px-4 py-2 rounded-full text-sm font-inter transition-colors ${
                  !selectedNumero
                    ? 'bg-red-700 text-white'
                    : 'bg-stone-200 text-stone-700 hover:bg-stone-300'
                }`}
              >
                Tous les articles
              </button>
              {numeros.map((n, idx) => (
                <button
                  key={idx}
                  onClick={() => setSelectedNumero(n.numero)}
                  className={`px-4 py-2 rounded-full text-sm font-inter transition-colors ${
                    selectedNumero === n.numero
                      ? 'bg-red-700 text-white'
                      : 'bg-stone-200 text-stone-700 hover:bg-stone-300'
                  }`}
                >
                  {n.numero} {n.date && `• ${n.date}`}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Articles Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-700 mx-auto"></div>
            <p className="mt-4 text-stone-500 font-inter">Chargement des archives...</p>
          </div>
        ) : filteredArticles.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg">
            <Archive className="h-16 w-16 text-stone-300 mx-auto mb-4" />
            <p className="text-stone-500 font-inter">Aucun article trouvé dans les archives.</p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-playfair text-xl font-semibold text-stone-900">
                {selectedNumero ? `Articles du ${selectedNumero}` : 'Tous les articles archivés'}
              </h2>
              <span className="text-sm text-stone-500 font-inter">
                {filteredArticles.length} article{filteredArticles.length > 1 ? 's' : ''}
              </span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredArticles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          </>
        )}

        {/* Call to action */}
        <div className="mt-16 bg-white rounded-lg p-8 text-center border border-stone-200">
          <h3 className="font-playfair text-2xl font-semibold text-stone-900 mb-3">
            Vous possédez d'anciens numéros ?
          </h3>
          <p className="font-lora text-stone-600 mb-6 max-w-2xl mx-auto">
            Aidez-nous à reconstituer l'histoire de VersaillesPlus ! Si vous possédez des 
            exemplaires papier de nos anciens numéros, contactez-nous pour contribuer à la 
            numérisation de nos archives.
          </p>
          <Link 
            to="/mentions-legales" 
            className="inline-flex items-center gap-2 bg-red-700 text-white px-6 py-3 rounded-sm font-inter text-sm hover:bg-red-800 transition-colors"
          >
            Nous contacter
            <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
